import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import api, { API_URL, fileUrl, formatApiError, getToken } from "@/lib/api";
import Layout, { PageTitle } from "@/components/Layout";
import { toast } from "sonner";
import { BookOpen, ArrowLeft, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Bookmark, Loader2, Lock } from "lucide-react";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const KELAS_TEKS = ["PAUD", "TKLB", "Kelas I", "Kelas II", "Kelas III", "Kelas IV", "Kelas V", "Kelas VI", "Kelas VII", "Kelas VIII", "Kelas IX", "Kelas X", "Kelas XI", "Kelas XII"];
const JENIS_UMUM = ["Fiksi", "Non-Fiksi", "Referensi", "Komik", "Majalah", "Ensiklopedia"];

/** Read-only Buku Digital list view for Guru / Ortu / Kepsek. */
export function BukuDigitalReadonlyList({ nav, basePath, title = "Daftar Buku Digital" }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [penulis, setPenulis] = useState("");
  const [kat, setKat] = useState("");
  const [jenis, setJenis] = useState("");
  const [applied, setApplied] = useState({ q: "", penulis: "", kat: "", jenis: "" });
  const navigate = useNavigate();

  const load = useCallback(async () => {
    const params = { format: "digital" };
    if (applied.q) params.q = applied.q;
    if (applied.penulis) params.penulis = applied.penulis;
    if (applied.kat) params.kategori = applied.kat;
    if (applied.jenis) params.jenis_buku = applied.jenis;
    const { data } = await api.get("/buku", { params });
    setItems(data);
  }, [applied]);
  useEffect(() => { load(); }, [load]);

  const apply = () => setApplied({ q, penulis, kat, jenis });
  const reset = () => { setQ(""); setPenulis(""); setKat(""); setJenis(""); setApplied({ q: "", penulis: "", kat: "", jenis: "" }); };

  const jenisOptions = useMemo(() => {
    if (kat === "buku_teks_pelajaran") return KELAS_TEKS;
    if (kat === "buku_umum") return JENIS_UMUM;
    return [...KELAS_TEKS, ...JENIS_UMUM];
  }, [kat]);

  return (
    <Layout nav={nav}>
      <PageTitle title={title} />
      <section className="pp-card p-5 mb-6 grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <input className="pp-input" placeholder="Cari judul" value={q} onChange={(e) => setQ(e.target.value)} data-testid="ro-filter-judul" />
        <input className="pp-input" placeholder="Penulis" value={penulis} onChange={(e) => setPenulis(e.target.value)} data-testid="ro-filter-penulis" />
        <select className="pp-input" value={kat} onChange={(e) => { setKat(e.target.value); setJenis(""); }} data-testid="ro-filter-kategori">
          <option value="">Semua kategori</option>
          <option value="buku_teks_pelajaran">Buku Teks</option>
          <option value="buku_umum">Buku Umum</option>
        </select>
        <select className="pp-input" value={jenis} onChange={(e) => setJenis(e.target.value)} data-testid="ro-filter-jenis">
          <option value="">Semua jenis / kelas</option>
          {jenisOptions.map(j => <option key={j}>{j}</option>)}
        </select>
        <div className="flex gap-2">
          <button onClick={apply} className="pp-btn-primary flex-1" data-testid="ro-filter-apply">Terapkan</button>
          <button onClick={reset} className="pp-btn-secondary flex-1" data-testid="ro-filter-reset">Reset</button>
        </div>
      </section>

      <section className="pp-card pp-freeze overflow-hidden">
        <table className="w-full text-sm" data-testid="ro-buku-digital-table">
          <thead><tr className="pp-table-header">
            <th className="text-left px-5 py-3">Cover</th>
            <th className="text-left px-5 py-3">Judul</th>
            <th className="text-left px-5 py-3">Penulis</th>
            <th className="text-left px-5 py-3">Kategori</th>
            <th className="text-left px-5 py-3">Jenis / Kelas</th>
            <th className="text-right px-5 py-3">Aksi</th>
          </tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-slate-400 italic">Belum ada buku digital.</td></tr>}
            {items.map(b => (
              <tr key={b.id} className="pp-row">
                <td className="px-5 py-3">{b.cover_file_id ? <img src={fileUrl(b.cover_file_id)} className="w-10 h-14 object-cover rounded" alt="" /> : <div className="w-10 h-14 bg-slate-100 rounded flex items-center justify-center"><BookOpen className="w-5 h-5 text-slate-400" /></div>}</td>
                <td className="px-5 py-3 font-medium">{b.judul}</td>
                <td className="px-5 py-3 text-slate-600">{b.penulis}</td>
                <td className="px-5 py-3 text-xs">{b.kategori.replace("_", " ")}</td>
                <td className="px-5 py-3 text-xs">{b.jenis_buku}</td>
                <td className="px-5 py-3 text-right">
                  <button onClick={() => navigate(`${basePath}/baca/${b.id}`)} className="pp-btn-primary" data-testid={`ro-baca-${b.id}`}>
                    <BookOpen className="w-3.5 h-3.5" />Baca
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </Layout>
  );
}

/** Read-only Buku Digital reader with bookmark & resume support. */
export function BukuDigitalReader({ nav, backPath }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [buku, setBuku] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [page, setPage] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [error, setError] = useState(null);
  const [initialBookmark, setInitialBookmark] = useState(null); // {page, updated_at}
  const saveTimer = useRef(null);
  const bookmarkedRef = useRef(null); // last page value saved to server, prevents redundant PUTs

  // Load metadata + bookmark
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [{ data: books }, { data: bm }] = await Promise.all([
          api.get("/buku", { params: { format: "digital" } }),
          api.get(`/buku/${id}/bookmark`).catch(() => ({ data: { page: 1 } })),
        ]);
        if (!alive) return;
        setBuku((books || []).find((b) => b.id === id) || null);
        setInitialBookmark(bm || { page: 1 });
        bookmarkedRef.current = (bm && bm.page) || 1;
      } catch { /* ignore */ }
    })();
    return () => { alive = false; };
  }, [id]);

  // Stream PDF as blob so the built-in viewer / right-click never expose a direct URL
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    const t = getToken();
    fetch(`${API_URL}/buku/${id}/pdf`, {
      headers: t ? { Authorization: `Bearer ${t}` } : {},
      credentials: "include",
    })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.blob(); })
      .then((blob) => { if (!cancelled) setBlobUrl(URL.createObjectURL(blob)); })
      .catch((e) => setError(e.message));
    return () => { cancelled = true; };
  }, [id]);

  // Revoke blob URL on unmount
  useEffect(() => () => { if (blobUrl) URL.revokeObjectURL(blobUrl); }, [blobUrl]);

  // Block right-click + Ctrl+S/P/C
  useEffect(() => {
    const stopCtx = (e) => e.preventDefault();
    const stopKeys = (e) => {
      const k = e.key?.toLowerCase();
      if ((e.ctrlKey || e.metaKey) && ["s", "p", "c"].includes(k)) {
        e.preventDefault(); e.stopPropagation();
      }
    };
    document.addEventListener("contextmenu", stopCtx);
    window.addEventListener("keydown", stopKeys, true);
    return () => {
      document.removeEventListener("contextmenu", stopCtx);
      window.removeEventListener("keydown", stopKeys, true);
    };
  }, []);

  // When document loads, jump to bookmarked page (if any)
  const onDocLoad = useCallback(({ numPages }) => {
    setNumPages(numPages);
    const target = (initialBookmark && initialBookmark.page) || 1;
    setPage(Math.min(Math.max(1, target), numPages));
  }, [initialBookmark]);

  // Debounced bookmark save on page change
  useEffect(() => {
    if (!numPages || !page) return;
    if (bookmarkedRef.current === page) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await api.put(`/buku/${id}/bookmark`, { page });
        bookmarkedRef.current = page;
      } catch { /* silent */ }
    }, 800);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [id, page, numPages]);

  const prev = () => setPage((p) => Math.max(1, p - 1));
  const next = () => setPage((p) => Math.min(numPages, p + 1));
  const zoomIn = () => setScale((s) => Math.min(2.5, s + 0.2));
  const zoomOut = () => setScale((s) => Math.max(0.5, s - 0.2));

  const jumpToBookmark = async () => {
    try {
      const { data } = await api.get(`/buku/${id}/bookmark`);
      const p = Math.min(Math.max(1, data.page || 1), numPages || 1);
      setPage(p);
      toast.success(`Lanjut ke halaman ${p}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const resumeHint = useMemo(() => {
    if (!initialBookmark || !initialBookmark.page || initialBookmark.page <= 1) return null;
    return initialBookmark.page;
  }, [initialBookmark]);

  return (
    <Layout nav={nav}>
      <PageTitle
        title={buku ? `Baca — ${buku.judul}` : "Baca Buku Digital"}
        action={
          <button onClick={() => navigate(backPath)} className="pp-btn-secondary" data-testid="reader-back">
            <ArrowLeft className="w-4 h-4" /> Kembali
          </button>
        }
      />

      {buku && (
        <div className="pp-card p-4 mb-4 text-sm text-slate-700 flex flex-wrap items-center gap-4">
          <div><span className="text-slate-500">Penulis:</span> <b>{buku.penulis}</b></div>
          <div><span className="text-slate-500">Kategori:</span> <b>{buku.kategori.replace("_", " ")}</b></div>
          <div><span className="text-slate-500">Jenis:</span> <b>{buku.jenis_buku}</b></div>
          {resumeHint && (
            <button
              onClick={jumpToBookmark}
              className="ml-auto inline-flex items-center gap-1 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 rounded-lg px-3 py-1.5 text-xs font-semibold"
              data-testid="resume-bookmark-btn"
            >
              <Bookmark className="w-3.5 h-3.5" /> Lanjut dari halaman {resumeHint}
            </button>
          )}
        </div>
      )}

      <div
        className="pp-card overflow-hidden select-none"
        style={{ userSelect: "none", WebkitUserSelect: "none" }}
        onDragStart={(e) => e.preventDefault()}
        data-testid="ro-pdf-reader"
      >
        <div className="flex flex-wrap items-center justify-between gap-2 bg-slate-900 text-white px-4 py-2 text-sm">
          <div className="flex items-center gap-2 text-amber-300">
            <Lock className="w-4 h-4" />
            <span className="text-xs">Buku digital ini view-only — tidak bisa diunduh, dicetak, atau disalin.</span>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={prev} disabled={page <= 1} className="p-1.5 rounded hover:bg-slate-800 disabled:opacity-30" data-testid="pdf-prev"><ChevronLeft className="w-4 h-4" /></button>
            <span className="font-mono text-xs px-2" data-testid="pdf-pageinfo">Hlm {page} / {numPages || "—"}</span>
            <button onClick={next} disabled={!numPages || page >= numPages} className="p-1.5 rounded hover:bg-slate-800 disabled:opacity-30" data-testid="pdf-next"><ChevronRight className="w-4 h-4" /></button>
            <div className="w-px h-5 bg-slate-700 mx-2" />
            <button onClick={zoomOut} disabled={scale <= 0.5} className="p-1.5 rounded hover:bg-slate-800 disabled:opacity-30" data-testid="pdf-zoom-out"><ZoomOut className="w-4 h-4" /></button>
            <span className="font-mono text-xs px-1">{Math.round(scale * 100)}%</span>
            <button onClick={zoomIn} disabled={scale >= 2.5} className="p-1.5 rounded hover:bg-slate-800 disabled:opacity-30" data-testid="pdf-zoom-in"><ZoomIn className="w-4 h-4" /></button>
          </div>
        </div>

        <div className="bg-slate-100 overflow-auto p-4" style={{ maxHeight: "78vh" }}>
          {error && <div className="text-rose-600 text-center py-16">Gagal memuat: {error}</div>}
          {!blobUrl && !error && (
            <div className="flex items-center justify-center text-slate-500 py-16 gap-2">
              <Loader2 className="w-5 h-5 animate-spin" /> Memuat buku…
            </div>
          )}
          {blobUrl && (
            <div className="flex justify-center">
              <Document
                file={blobUrl}
                onLoadSuccess={onDocLoad}
                onLoadError={(e) => setError(e.message)}
                loading={<div className="text-slate-500 py-16">Memproses PDF…</div>}
              >
                <Page
                  pageNumber={page}
                  scale={scale}
                  renderAnnotationLayer={false}
                  renderTextLayer={false}
                  className="shadow-xl"
                />
              </Document>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
